import React, { useId } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import { useOnHover } from '@/hooks/useOnHover';

import colors from '@/config/colors';
import styles from './styles';

interface SelectButtonProps {
  disabled?: boolean;
  onValueChange: (value: SelectButtonProps['value']) => void;
  options: {
    label: string;
    value: SelectButtonProps['value'];
  }[];
  value: number | string;
}

const SelectButton = ({
  disabled = false,
  onValueChange,
  options,
  value,
}: SelectButtonProps) => {
  const id = useId();

  function handlePressOption(value: SelectButtonProps['value']) {
    onValueChange(value);
  }

  return (
    <View style={{ flexDirection: 'row' }}>
      {options.map((option, index) => (
        <SelectButtonOption
          handlePressOption={handlePressOption}
          id={id}
          index={index}
          option={option}
          options={options}
          value={value}
          disabled={disabled}
          key={index}
        />
      ))}
    </View>
  );
};

const SelectButtonOption = ({
  handlePressOption,
  disabled,
  id,
  index,
  option,
  options,
  value,
}: {
  handlePressOption: (value: string | number) => void;
  id: string;
  index: number;
  option: { label: string; value: string | number };
  options: { label: string; value: string | number }[];
  value: number | string;
  disabled?: boolean;
}) => {
  const { isHovered, handleHoverIn, handleHoverOut } = useOnHover(disabled);

  return (
    <Pressable
      key={`${id}${index}`}
      onPress={() => handlePressOption(option.value)}
      style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      disabled={disabled}
    >
      <View
        style={[
          styles.wrapper,
          options.length === 1 && { borderRadius: 8, borderRightWidth: 1 },
          disabled && { opacity: 0.7 },
          {
            borderColor: disabled
              ? colors.newUI.therapist.grey60
              : colors.newUI.therapist.blue90,
          },
          isHovered &&
            !disabled && {
              backgroundColor: colors.newUI.therapist.primaryBlue10,
            },
          value === option.value && {
            backgroundColor: disabled
              ? colors.newUI.therapist.grey30
              : colors.newUI.therapist.blue20,
          },
          index === 0
            ? styles.firstButtonBorder
            : index === options.length - 1
              ? styles.lastButtonBorder
              : {},
          options.length === 1 && { borderRightWidth: 1, borderRadius: 8 },
        ]}
      >
        <View
          style={[
            styles.textWrapper,
            {
              borderColor: disabled
                ? colors.newUI.therapist.grey60
                : colors.newUI.therapist.blue90,
            },
            index < options.length - 1 &&
              index > 0 && {
                borderRightWidth: 1,
                borderLeftWidth: 0,
              },
            index === 1 && { borderLeftWidth: 1 },
          ]}
        >
          <Text
            style={[
              styles.label,
              value === option.value && {
                color: disabled
                  ? colors.newUI.therapist.grey70
                  : colors.newUI.therapist.blue90,
                fontWeight: 600,
              },
            ]}
          >
            {option.label}
          </Text>
        </View>
      </View>
    </Pressable>
  );
};

export default SelectButton;
